import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { User, CreditCard, Shield, AlertTriangle, Calendar, ArrowRight, Check } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Account() {
  const { user, getAuthHeader, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [subInfo, setSubInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    fetchSubInfo();
  }, []);

  const fetchSubInfo = async () => {
    try {
      const res = await axios.get(`${API}/subscription/info`, { headers: getAuthHeader() });
      setSubInfo(res.data);
    } catch (err) {
      toast.error("Erreur lors du chargement des informations");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    setCancelling(true);
    try {
      const res = await axios.post(`${API}/stripe/cancel-subscription`, {}, { headers: getAuthHeader() });
      toast.success(res.data.message);
      setShowConfirm(false);
      fetchSubInfo();
      if (refreshUser) refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'annulation");
    } finally {
      setCancelling(false);
    }
  };

  const planColors = {
    free: 'bg-stone-100 text-stone-700',
    starter: 'bg-blue-50 text-blue-700',
    professional: 'bg-purple-50 text-purple-700',
  };

  const statusLabels = {
    active: 'Actif',
    trial: 'Actif',
    cancelled: 'Annulé',
    expired: 'Expiré',
  };

  const formatDate = (val) => {
    if (!val) return '-';
    const d = typeof val === 'number' ? new Date(val * 1000) : new Date(val);
    return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center">
        <div className="w-8 h-8 border-3 border-[#005F73] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF8] py-8 px-4" data-testid="account-page">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <User className="w-7 h-7 text-[#005F73]" />
            <h1 className="text-2xl md:text-3xl font-bold text-[#005F73]">Mon compte</h1>
          </div>
          <p className="text-stone-500">Gérez votre profil et votre abonnement.</p>
        </div>

        {/* Profile info */}
        <div className="bg-white rounded-xl border border-stone-200 p-6 mb-6 shadow-sm" data-testid="profile-card">
          <h2 className="text-lg font-semibold text-[#005F73] mb-4 flex items-center gap-2">
            <User className="w-5 h-5" />
            Informations personnelles
          </h2>
          <div className="space-y-3">
            <div className="flex justify-between items-center py-2 border-b border-stone-50">
              <span className="text-stone-500 text-sm">Nom</span>
              <span className="font-medium text-stone-800" data-testid="profile-name">{user?.full_name}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-stone-50">
              <span className="text-stone-500 text-sm">Email</span>
              <span className="font-medium text-stone-800" data-testid="profile-email">{user?.email}</span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-stone-500 text-sm">Membre depuis</span>
              <span className="font-medium text-stone-800">{formatDate(subInfo?.created_at)}</span>
            </div>
          </div>
        </div>

        {/* Subscription info */}
        <div className="bg-white rounded-xl border border-stone-200 p-6 mb-6 shadow-sm" data-testid="subscription-card">
          <h2 className="text-lg font-semibold text-[#005F73] mb-4 flex items-center gap-2">
            <CreditCard className="w-5 h-5" />
            Abonnement
          </h2>

          <div className="space-y-4">
            {/* Plan badge */}
            <div className="flex items-center justify-between">
              <span className="text-stone-500 text-sm">Plan actuel</span>
              <span className={`px-3 py-1 rounded-full text-sm font-semibold ${planColors[subInfo?.plan] || planColors.free}`} data-testid="plan-badge">
                {subInfo?.plan_name}
              </span>
            </div>

            {/* Price */}
            {subInfo?.plan_price > 0 && (
              <div className="py-2 border-b border-stone-50">
                <div className="flex items-center justify-between">
                  <span className="text-stone-500 text-sm">Prix</span>
                  <span className="font-medium text-stone-800">
                    {subInfo.plan_price} EUR{subInfo.plan === 'free' ? ' (paiement unique)' : '/mois'}
                  </span>
                </div>
                {subInfo.plan !== 'free' && (
                  <p className="text-xs text-stone-400 mt-1">Renouvellement automatique chaque mois</p>
                )}
              </div>
            )}

            {/* Status */}
            <div className="flex items-center justify-between py-2 border-b border-stone-50">
              <span className="text-stone-500 text-sm">Statut</span>
              <span className={`font-medium ${subInfo?.subscription_status === 'active' ? 'text-green-600' : subInfo?.subscription_status === 'cancelled' ? 'text-red-500' : 'text-amber-600'}`} data-testid="subscription-status">
                {statusLabels[subInfo?.subscription_status] || subInfo?.subscription_status}
              </span>
            </div>

            {/* Cancellation end date */}
            {subInfo?.cancel_at_period_end && subInfo?.subscription_ends_at && (
              <div className="bg-red-50 rounded-lg p-4 border border-red-100" data-testid="cancel-notice">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-medium text-red-700">Abonnement annule</p>
                    <p className="text-sm text-red-600 mt-1">
                      Vous conservez l'acces a votre plan jusqu'au <strong>{formatDate(subInfo.subscription_ends_at)}</strong>.
                      Après cette date, votre compte passera à l'offre Découverte.
                    </p>
                  </div>
                </div>
              </div>
            )}

          </div>

          {/* Action buttons */}
          <div className="mt-6 pt-4 border-t border-stone-100 space-y-3">
            {/* Upgrade button for free/starter users */}
            {['free', 'starter'].includes(subInfo?.plan) && (
              <Button
                onClick={() => navigate('/pricing')}
                className="w-full bg-[#005F73] hover:bg-[#005F73]/90 text-white rounded-full"
                data-testid="upgrade-btn"
              >
                <ArrowRight className="w-4 h-4 mr-2" />
                Passer à un plan supérieur
              </Button>
            )}

            {/* Cancel button for paid active subscriptions */}
            {subInfo?.plan !== 'free' && subInfo?.subscription_status === 'active' && !subInfo?.cancel_at_period_end && (
              <>
                {!showConfirm ? (
                  <Button
                    variant="outline"
                    onClick={() => setShowConfirm(true)}
                    className="w-full rounded-full border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                    data-testid="cancel-subscription-btn"
                  >
                    Annuler mon abonnement
                  </Button>
                ) : (
                  <div className="bg-red-50 rounded-xl p-5 border border-red-200" data-testid="cancel-confirm-dialog">
                    <p className="font-medium text-red-700 mb-2">Confirmer l'annulation ?</p>
                    <p className="text-sm text-red-600 mb-4">
                      Vous conserverez l'accès à votre plan actuel jusqu'à la fin de la période de facturation en cours. Après cela, votre compte passera à l'offre Découverte.
                    </p>
                    <div className="flex gap-3">
                      <Button
                        onClick={handleCancel}
                        disabled={cancelling}
                        className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-full"
                        data-testid="confirm-cancel-btn"
                      >
                        {cancelling ? 'Annulation...' : 'Oui, annuler'}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setShowConfirm(false)}
                        className="flex-1 rounded-full"
                        data-testid="keep-subscription-btn"
                      >
                        Non, garder
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Plan features */}
        <div className="bg-white rounded-xl border border-stone-200 p-6 shadow-sm" data-testid="plan-features">
          <h2 className="text-lg font-semibold text-[#005F73] mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Fonctionnalités de votre plan
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {(subInfo?.plan === 'free' ? ['1 simulation incluse', 'Calculs complets', 'Visualisations', 'Paiement unique'] :
              subInfo?.plan === 'starter' ? ['5 projets/mois', 'Calculs complets', 'Visualisations', 'Export PDF'] :
              ['Projets illimités', 'Calculs complets', 'Visualisations', 'Export PDF', 'Comparaison']
            ).map((feature, i) => (
              <div key={i} className="flex items-center gap-2 py-1.5">
                <Check className="w-4 h-4 text-[#0A9396] shrink-0" />
                <span className="text-sm text-stone-600">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
